'use client';

import type { ReactNode } from 'react';
import { InfoIcon } from '@phosphor-icons/react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { track, type WidgetId } from '@/lib/telemetry';
import { cn } from '@/lib/utils';

/**
 * The home widgets' "what do these numbers mean?" affordance: a small ⓘ that
 * opens a plain-language explanation, so the card itself stays lean (the
 * headline numbers keep no inline glossary). `triggerClassName` adapts the
 * icon to the card it sits on (the dark hero vs. a normal surface card).
 */
export function WidgetInfoButton({
  widget,
  label,
  triggerClassName,
  children,
}: {
  /** Analytics id, e.g. "ritmo" — mirrors the other widget_action events. */
  widget: WidgetId;
  /** Accessible name, e.g. "Sobre os valores de Ritmo de gastos". */
  label: string;
  triggerClassName?: string;
  children: ReactNode;
}): ReactNode {
  const handleOpenChange = (open: boolean): void => {
    if (open) track('new_home.widget_action', { widget, action: 'info' });
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            '-m-1.5 shrink-0 cursor-pointer rounded-full p-1.5 transition-colors',
            triggerClassName ?? 'text-text-tertiary hover:text-text-secondary',
          )}
        >
          <InfoIcon size={15} aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        collisionPadding={12}
        className="w-72 rounded-xl p-4"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** One aligned "label — value" line inside an info popover. */
export function WidgetInfoRow({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-text-secondary">{label}</dt>
      <dd className="shrink-0 font-mono text-xs text-text-primary tabular-nums">{value}</dd>
    </div>
  );
}
